import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { PatientService } from "../../../../services/api/patient.service";
import { PatientDetail } from "../types";

interface EditPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: PatientDetail;
  reload: () => void;
}

export const EditPatientModal: React.FC<EditPatientModalProps> = ({
  isOpen,
  onClose,
  patient,
  reload,
}) => {
  const [editPatientName, setEditPatientName] = useState("");
  const [editPatientGender, setEditPatientGender] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && patient) {
      setEditPatientName(patient.name || "");
      setEditPatientGender(patient.gender || "Male");
    }
  }, [isOpen, patient]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!editPatientName.trim()) {
      toast.error("Please enter patient name");
      return;
    }

    setIsUpdating(true);
    try {
      const response = await PatientService.updatePatient(patient.id, {
        name: editPatientName,
        gender: editPatientGender,
      } as any);
      if (response.success) {
        toast.success("Patient data updated successfully");
        onClose();
        reload();
      } else {
        toast.error(response.message || "Failed to update patient data");
      }
    } catch (error) {
      toast.error("Failed to update patient data");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden border border-gray-100">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Edit Patient Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:bg-gray-100 hover:text-gray-600 p-1.5 rounded-lg transition-colors"
            disabled={isUpdating}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-3.5">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Mobile Number
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 cursor-not-allowed"
              value={patient.mobileNumber}
              readOnly
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Patient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white font-medium text-gray-900"
              placeholder="Enter patient name"
              value={editPatientName}
              onChange={(e) => setEditPatientName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white font-medium text-gray-900"
              value={editPatientGender}
              onChange={(e) => setEditPatientGender(e.target.value)}
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Age
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 cursor-not-allowed"
              value={patient.age}
              readOnly
            />
          </div>
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 gap-3 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            disabled={isUpdating}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2 bg-emerald-600 rounded-lg text-xs font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
            disabled={isUpdating || !editPatientName.trim()}
          >
            {isUpdating ? "Updating..." : "Update Details"}
          </button>
        </div>
      </div>
    </div>
  );
};
